/**
 * The brief - what changed since you last looked.
 *
 * SITS ABOVE THE BOARD, AND ONLY UNTIL YOU DISMISS IT.
 *
 * It is not a page. Making it a page means somebody has to remember to visit it,
 * and the whole point is that it finds you. It is also not a modal: a modal that
 * appears over a monitoring board at 7am is a thing people learn to close before
 * reading, and it would cover the very rows it is talking about.
 *
 * DISMISSING IS THE ONLY THING THAT MOVES THE MARK. Closing the tab, refreshing,
 * or opening a second window all leave the window where it was, because the
 * alternative - advancing on read - loses a day of changes silently, and there
 * is no way afterwards to tell that it happened.
 *
 * The counts and machine names here are computed server-side from Firestore. The
 * paragraph at the top is the only part written by the model, and when the model
 * is unreachable that paragraph is simply absent - everything else still renders.
 */

import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { errText } from './btApi';

function call<TIn extends object, TOut>(name: string) {
  return async (data: TIn): Promise<TOut> => (await httpsCallable<TIn, TOut>(functions, name)(data)).data;
}

interface Change {
  targetId: string; name: string; client: string; source: string;
  from: string; to: string; reason: string; at: number;
}
interface Action {
  machines: string[]; title: string; meaning: string; whatToDo: string;
  severity: 'critical' | 'warning' | 'info';
}
interface Brief {
  since: number; until: number; firstEver: boolean;
  positives: Change[]; negatives: Change[]; neutrals: Change[];
  actions: Action[]; narrative: string; truncated: boolean;
}

const getBrief = call<{ orgId: string }, Brief>('getBrief');
const markBriefSeen = call<{ orgId: string }, { ok: boolean }>('markBriefSeen');

/** "since Friday afternoon" reads better than a timestamp nobody converts. */
function windowText(since: number, until: number, firstEver: boolean): string {
  if (firstEver) return 'the last 24 hours';
  const h = (until - since) / 3_600_000;
  if (h < 1.5) return 'the last hour';
  if (h < 20) return `the last ${Math.round(h)} hours`;
  const d = Math.round(h / 24);
  return d <= 1 ? 'since yesterday' : `the last ${d} days`;
}

function when(ms: number): string {
  const mins = (Date.now() - ms) / 60000;
  if (mins < 60) return `${Math.max(1, Math.round(mins))}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
}

function Group({ title, tone, rows }: { title: string; tone: string; rows: Change[] }) {
  if (!rows.length) return null;
  return (
    <div className="brief-group">
      <div className={'brief-group-title ' + tone}>{title} <span className="dim">({rows.length})</span></div>
      <ul className="brief-list">
        {rows.slice(0, 5).map((c) => (
          <li key={c.targetId + c.at}>
            <span className="brief-machine">{c.name}</span>
            {c.client && <span className="dim"> &middot; {c.client}</span>}
            <span className="dim"> &middot; {when(c.at)}</span>
            <div className="brief-reason">{c.reason}</div>
          </li>
        ))}
        {rows.length > 5 && (
          // The brief is a brief. Twelve rows a column at 108 transitions filled
          // the screen and pushed the actual board below the fold, which is the
          // opposite of helping somebody start their morning.
          <li className="dim">and {rows.length - 5} more &mdash; see Status changes.</li>
        )}
      </ul>
    </div>
  );
}

export function BriefPanel({ orgId }: { orgId: string | null }) {
  const [brief, setBrief] = useState<Brief | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [gone, setGone] = useState(false);
  const [busy, setBusy] = useState(false);
  /**
   * COLLAPSED BY DEFAULT.
   *
   * At 109 changes the expanded brief filled the screen and pushed the board -
   * the thing people came for - below the fold. A summary that has to be
   * scrolled past to reach the work is not a summary, it is an obstacle.
   *
   * The collapsed bar still carries the counts, because the decision it has to
   * support is "is it worth opening", and that decision needs a number. A bar
   * saying only "What changed" makes you open it to find out there was nothing.
   */
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    let live = true;
    void (async () => {
      try {
        const b = await getBrief({ orgId });
        if (live) setBrief(b);
      } catch (e) {
        // A brief that cannot load must not take the board with it. The board is
        // the product; this is the summary of it.
        if (live) setErr(errText(e));
      }
    })();
    return () => { live = false; };
  }, [orgId]);

  if (!orgId || gone) return null;
  if (err) return null;

  /*
   * A PLACEHOLDER, NOT AN EMPTY SPACE.
   *
   * The brief is computed on demand and the paragraph at the top costs a model
   * call, so a cold open takes seconds. Rendering null for that whole time
   * meant the bar appeared out of nowhere, after the board had already settled
   * - which reads as the page glitching rather than as something arriving, and
   * gives somebody who opens the dashboard at 7am no reason to wait for it.
   *
   * It occupies the same slot with the same height, so nothing below it moves
   * when the real bar replaces it. A skeleton that changes the layout on
   * arrival is worse than no skeleton: it moves the row you were about to click.
   */
  if (!brief) {
    return (
      <section className="brief brief-loading" aria-busy="true">
        <div className="brief-head">
          <h2 className="brief-title dim">Working out what changed...</h2>
        </div>
      </section>
    );
  }

  const nothing = !brief.negatives.length && !brief.positives.length && !brief.neutrals.length;
  const win = windowText(brief.since, brief.until, brief.firstEver);

  const dismiss = () => {
    setBusy(true);
    void (async () => {
      try { await markBriefSeen({ orgId }); } catch { /* the mark is a convenience, not a guarantee */ }
      setGone(true);
    })();
  };

  return (
    <section className={'brief' + (brief.negatives.length ? ' brief-bad' : '')}>
      <div className="brief-head">
        <h2 className="brief-title">
          {nothing ? `Nothing changed in ${win}` : `What changed in ${win}`}
        </h2>

        {/* The counts live in the COLLAPSED bar, not only inside it. They are
            what the Show decision is made on - a bar saying only "What
            changed" makes you open it to discover there was nothing. */}
        {!open && !nothing && (
          <span className="brief-counts">
            {brief.negatives.length > 0 && (
              <span className="brief-count bad">{brief.negatives.length} need attention</span>
            )}
            {brief.positives.length > 0 && (
              <span className="brief-count good">{brief.positives.length} recovered</span>
            )}
            {brief.actions.length > 0 && (
              <span className="brief-count">
                {brief.actions.length} known {brief.actions.length === 1 ? 'fix' : 'fixes'}
              </span>
            )}
          </span>
        )}

        <span className="brief-actions-row">
          {!nothing && (
            <button className={open ? 'btn-link' : 'btn'} onClick={() => setOpen((o) => !o)}>
              {open ? 'Hide' : 'Show'}
            </button>
          )}
          <button className="btn-link" onClick={dismiss} disabled={busy}>
            {busy ? 'Marking...' : 'Got it'}
          </button>
        </span>
      </div>

      {open && brief.narrative && <p className="brief-narrative">{brief.narrative}</p>}

      {nothing && !open && !brief.narrative && (
        <p className="brief-narrative dim">
          No machine changed status. That is the good outcome, and it is worth one line rather than
          a blank space.
        </p>
      )}

      {open && (
      <div className="brief-cols">
        <Group title="Needs attention" tone="bad" rows={brief.negatives} />
        <Group title="Recovered" tone="good" rows={brief.positives} />
        <Group title="Moved sideways" tone="mid" rows={brief.neutrals} />
      </div>
      )}

      {open && brief.actions.length > 0 && (
        <div className="brief-actions">
          <div className="brief-group-title">What to do</div>
          {brief.actions.map((a) => (
            <div className="brief-action" key={a.title}>
              <div className="brief-action-head">
                <span className={'tag tag-' + (a.severity === 'critical' ? 'bad' : a.severity === 'warning' ? 'warn' : 'ok')}>
                  {a.severity}
                </span>
                <strong>{a.title}</strong>
                <span className="dim">
                  {a.machines.length === 1 ? a.machines[0] : `${a.machines.length} machines`}
                </span>
              </div>
              <div className="brief-reason">{a.meaning}</div>
              <div className="brief-do">{a.whatToDo}</div>
            </div>
          ))}
        </div>
      )}

      {open && brief.truncated && (
        <p className="brief-reason dim">
          More changed than fits here. Status changes has the full list.
        </p>
      )}
    </section>
  );
}
